/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  Input,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { checkToken } from 'api/utils';
import {
  deletePercepcionFechaFijaPago,
  getPercepcionFechaFijaPago,
  getPercepcionFechaFijaPagos,
  patchPercepcionFechaFijaPago,
  postPercepcionFechaFijaPago,
} from 'api/nominas/percepciones/PercepcionFechaFijaPago';
import API from 'utils/api';
import { getEstadosGlobales } from 'api/catalogos';
import { getPeriodosDeNominas } from 'api/nominas/periodos/periodosDeNominas';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import { getPeriodosDeNominasDetalles } from 'api/nominas/periodos/periodosDeNominasDetalle';

export const percepcionClaseDeContratacionPermission = {
  permissionModel: 'percepcionclasedecontratacion',
  catalogsModels: [
    'percepcion',
  ],
  tabsModels: [],
};

const PercepcionFechaFijaPago = ({
  permission,
  selected,
  estatusAutorizadoInactivo,
  mostrarTabla,
}) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [pageActual, setPageActual] = useState(1);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [periodosNominas, setPeriodosNominas] = useState([]);
  const [periodo, setPeriodo] = useState();
  const [tiposNominasEmpleados, setTiposNominasEmpleados] = useState([]);
  const [estados, setEstados] = useState([]);
  const [periodosNominasDetalles, setPeriodosNominasDetalles] = useState([]);
  const [periodosFiltered, setPeriodosFiltered] = useState([]);

  const formInstances = [
    form,
    formDatos,
  ];
  const baseURI = '/nominas/percepciones-periodos-de-pago-para-percepciones-no-recurrentes';
  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);
      const response = await API.get(baseURI, { params: { page, ...q, percepcion: selected } });
      const { results } = response.data;
      setSelectedRowKeys([]);
      setPageActual(page);
      setData({ ...response.data, results });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    const _data = await API.get(baseURI, { params: { page, ...q } });
    const { results } = _data.data;
    setData({ ..._data.data, results });
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _periodosNominasDetalles,
        _periodosFiscales,
        _tiposNominasEmpleados,
        _periodosNominas,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodosDeNominasDetalles(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodosDeNominas({ estados_globales: ESTATUS_AUTORIZADO }),
        getPercepcionFechaFijaPagos({ percepcion: selected }),
      ]);
      const { results } = _data;
      if (mounted) {
        setPeriodosNominasDetalles(_periodosNominasDetalles);
        setPeriodosFiscales(_periodosFiscales);
        setTiposNominasEmpleados(_tiposNominasEmpleados);
        setEstados(_estados);
        setPeriodosNominas(_periodosNominas);
        setData({ ..._data, results });
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
    formDatos.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const match = responseData || (data?.results || []).find((item) => item.id === key);
      if (!match) return;
      const _claseContratacion = await getPercepcionFechaFijaPago(match.id);
      setTimeout(() => {
        if (_claseContratacion) {
          form.resetFields();
          formDatos.resetFields();
          setTimeout(() => {
            form.setFieldsValue(formatReceived(_claseContratacion));
            formDatos.setFieldsValue(formatReceived(_claseContratacion));
            setVisible(true);
          });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchPercepcionFechaFijaPago(key, { ...values, percepcion: selected }) : postPercepcionFechaFijaPago({ ...values, percepcion: selected }));
      if (saved) {
        if (_continue) {
          await fetchData(1, { percepcion: selected });
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData(1, { percepcion: selected });
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionFechaFijaPago(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData(1, { percepcion: selected });
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };
  const filterPeriodos = (_data) => {
    const {
      tipo_de_nomina_empleado,
      periodo_fiscal,
    } = form.getFieldsValue();

    const _periodo = periodo_fiscal || _data?.periodo_fiscal;
    const _tipo_nomina = tipo_de_nomina_empleado || _data?.tipo_de_nomina_empleado;

    const _periodosNominas = periodosNominas.filter(
      (e) => e.tipo_de_nomina_empleado === _tipo_nomina
      && e.periodo_fiscal === _periodo,
    );

    const _periodosDetallesFiltered = periodosNominasDetalles.filter(
      (e) => _periodosNominas.some(((x) => x.id === e.periodo_de_nomina)),
    );

    setPeriodosFiltered(_periodosDetallesFiltered);
  };

  const columns = [
    // {
    //   titleText: 'Percepcion',
    //   dataIndex: 'percepcion',
    //   key: 'percepcion',
    //   width: 200,
    // },
    {
      titleText: 'Tipo de Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 300,
      render: (val) => {
        const tipo = tiposNominasEmpleados.find((e) => e.id === val);
        return tipo ? `${tipo.clave} - ${tipo.descripcion}` : 'Desconocido';
      },
    },
    {
      titleText: 'Periodo Fiscal',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 200,
      render: (val) => {
        const pf = periodosFiscales.find((e) => e.id === val);
        return pf ? `${pf.periodo}` : 'Desconocido';
      },
    },
    {
      titleText: 'Periodo de Nómina',
      dataIndex: 'periodos_de_nomina_detalle',
      key: 'periodos_de_nomina_detalle',
      width: 400,
      render: (val) => {
        const detalle = periodosNominasDetalles.find((e) => e.id === val);
        if (!detalle) return 'Desconocido';
        const periodoNomina = periodosNominas.find((e) => e.id === detalle.periodo_de_nomina);
        return periodoNomina
          ? `${periodoNomina.prefijo_foliador_nomina} (${detalle.fecha_inicial} al ${detalle.fecha_final})`
          : `(${detalle.fecha_inicial} al ${detalle.fecha_final})`;
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => {
        const estado = estados.find((e) => e.id === val);
        return estado ? estado.descripcion : 'Desconocido';
      },
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {mostrarTabla && (
        <Table
          cols={columns}
          data={data?.results || []}
          pagination={false}
          paginationCallback={fetchPaginated}
          count={data?.count || 0}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableAdd={estatusAutorizadoInactivo}
          disableDelete={estatusAutorizadoInactivo}
          disableEdit={estatusAutorizadoInactivo}
          allowDoubleClick={estatusAutorizadoInactivo === false}
          permission={permission}
          mobileColIndex={0}
          allowExportPDF={false}
        />
        )}
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Periodos`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="50%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="tipo_de_nomina_empleado"
                  label="Tipo de Empleado"
                  rules={[{ required: true, message: 'Campo requerido' }]}
                  hasFeedback
                >
                  <Select
                    keyLabelRender
                    showSearch
                    labelProp="descripcion"
                    dataSource={tiposNominasEmpleados}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="periodo_fiscal"
                  label="Periodo Fiscal"
                  rules={rules.estatus}
                  hasFeedback
                >
                  <Select
                    dataSource={periodosFiscales}
                    disabled={selectedRowKeys.length}
                    onChange={() => {
                      form.resetFields(['periodo_de_nomina_detalle', 'dias_calculados']);
                      filterPeriodos();
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="periodos_de_nomina_detalle"
                  label="Periodo"
                  rules={rules.estatus}
                  hasFeedback
                >
                  <Select
                    // mode="multiple"
                    keyProp="id"
                    labelProp="numero_de_nomina"
                    disabled={selectedRowKeys.length}
                    // eslint-disable-next-line max-len
                    dataSource={periodosNominasDetalles.filter((e) => periodosFiltered.some((x) => x.id === e.id))}
                    render={(value) => `${value?.numero_de_nomina} (${value?.fecha_inicial} al ${value?.fecha_final})`}
                    // disabled={isAdding || disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Estatus (solo lectura) */}
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Clase de Contratación ${
            data?.results?.find((e) => e.id === form.getFieldValue('clase_de_contratacion'))?.descripcion || ''
          }`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionFechaFijaPago;
